import { Button, cn, EmptyState, formatAge, Sheet, SidebarItem, TabPanel, Tabs } from "@nexxt/ui";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, type LinkOptions } from "@tanstack/react-router";
import { Bell as BellIcon, X } from "lucide-react";
import { useState } from "react";
import { errorText } from "../errors";
import type { FollowClient } from "../follow/follow";
import { FollowingList } from "../follow/following-list";
import { OWN_ROOM } from "../live/live-client";
import { useLiveRoom } from "../live/live-provider";
import { m } from "../paraglide/messages.js";
import type { PushClient } from "../push/push-device";
import { PushSection, usePushOpen } from "../push/push-section";
import type { BellItem } from "./bell-item";
import { bellBy, bellTitle } from "./bell-text";
import { ErrorLine } from "./error-line";

// The bell (ADR-280): what asks the user for an action, next to its email. The user's own room says when to read it
// again, so it reads nothing on a timer while the socket is open; while it is not, it reads every minute like every
// room (ADR-278). The items, their pages and the gateway are the app's (`BellClient`): Backstage and Atelier each
// link an item to their own page of what it is about. Next to the items, what the user follows (ADR-281); a row of a
// followed object is said in the app's words, with who changed it (ADR-308). At its foot, the pushes of this device, and
// a notification clicked opens the page of its row (ADR-283).

/** The bell as the app reaches it: its gateway, and the page of each item. */
export interface BellClient {
  list(cursor: string | undefined): Promise<{ items: BellItem[]; nextCursor: string | null }>;
  unread(): Promise<{ count: number }>;
  markRead(input: { ids: string[] } | { all: true }): Promise<{ count: number }>;
  /** The page of the app the item is about; null when it has none. A push says no more than these (ADR-283). */
  linkOf(item: Pick<BellItem, "templateCode" | "entityType" | "entityId">): LinkOptions | null;
  /** The pushes to the devices of the person (ADR-283). */
  push: PushClient;
}

/** Beyond this the bell says "99+": the gateway counts up to 100 (`UNREAD_COUNT_CAP`). */
const SHOWN_MAX = 99;
const BELL_KEY = ["bell"] as const;
const unreadKey = [...BELL_KEY, "unread"] as const;
const listKey = [...BELL_KEY, "list"] as const;

interface BellProps {
  client: BellClient;
  follows: FollowClient;
  /**
   * `item`: a place of the sidebar column, with its label; `icon`: the top bar below `lg`. One of the two at a time,
   * never inside the menu sheet, which would close the bell's own sheet with it.
   */
  variant: "item" | "icon";
  className?: string;
}

export function Bell({ client, follows, variant, className }: BellProps) {
  const [open, setOpen] = useState(false);
  useLiveRoom(OWN_ROOM, [BELL_KEY]);
  usePushOpen(client, BELL_KEY);
  const unread = useQuery({ queryKey: unreadKey, queryFn: () => client.unread() });
  const count = unread.data?.count ?? 0;
  const label = count > 0 ? m.bell_open_unread({ count: count > SHOWN_MAX ? `${SHOWN_MAX}+` : count }) : m.bell_label();
  const show = () => setOpen(true);

  return (
    <>
      {variant === "item" ? (
        <SidebarItem
          render={<button type="button" onClick={show} aria-haspopup="dialog" />}
          icon={<BellIcon aria-hidden />}
          label={m.bell_label()}
          count={count}
          countLabel={m.bell_unread_sr()}
          countMax={SHOWN_MAX}
        />
      ) : (
        <Button
          variant="ghost"
          size="icon"
          aria-label={label}
          aria-haspopup="dialog"
          onClick={show}
          className={cn("relative", className)}
        >
          <BellIcon aria-hidden />
          {count > 0 && <span aria-hidden className="absolute top-1.5 right-1.5 size-2 rounded-full bg-brand" />}
        </Button>
      )}
      <Sheet open={open} onOpenChange={setOpen} side="right" title={m.bell_label()} className="w-96 max-w-full">
        {open && <BellPanel client={client} follows={follows} unread={count} onClose={() => setOpen(false)} />}
      </Sheet>
    </>
  );
}

interface BellPanelProps {
  client: BellClient;
  follows: FollowClient;
  unread: number;
  onClose: () => void;
}

function BellPanel({ client, follows, unread, onClose }: BellPanelProps) {
  const [tab, setTab] = useState<"inbox" | "following">("inbox");
  const queryClient = useQueryClient();
  const list = useInfiniteQuery({
    queryKey: listKey,
    queryFn: ({ pageParam }) => client.list(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
  });
  const markRead = useMutation({
    mutationFn: client.markRead,
    onSuccess: (left) => queryClient.setQueryData(unreadKey, left),
    onSettled: () => queryClient.invalidateQueries({ queryKey: listKey }),
  });
  const items = list.data?.pages.flatMap((page) => page.items) ?? [];
  const opened = (item: BellItem) => {
    if (item.readAt === null) markRead.mutate({ ids: [item.id] });
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center gap-2 border-b border-border px-4 py-2">
        {/* the sheet's title names the dialog: this is its visible copy, not a second heading */}
        <p className="flex-1 text-base font-medium">{m.bell_label()}</p>
        {tab === "inbox" && (
          <Button
            variant="ghost"
            size="sm"
            disabled={unread === 0 || markRead.isPending}
            onClick={() => markRead.mutate({ all: true })}
          >
            {m.bell_mark_all()}
          </Button>
        )}
        <Button variant="ghost" size="icon" aria-label={m.bell_close()} onClick={onClose}>
          <X aria-hidden />
        </Button>
      </div>
      <Tabs
        value={tab}
        onValueChange={setTab}
        label={m.bell_label()}
        tabs={[
          { value: "inbox", label: m.bell_tab_inbox() },
          { value: "following", label: m.bell_tab_following() },
        ]}
        className="min-h-0 flex-1 gap-0 pt-1"
      >
        <TabPanel value="inbox" className="overflow-auto">
          {list.error && <ErrorLine className="px-4 py-3">{errorText(list.error)}</ErrorLine>}
          {markRead.error && <ErrorLine className="px-4 py-3">{errorText(markRead.error)}</ErrorLine>}
          {list.isSuccess && items.length === 0 && (
            <EmptyState title={m.bell_empty_title()}>{m.bell_empty_hint()}</EmptyState>
          )}
          <ul className="divide-y divide-border">
            {items.map((item) => (
              <li key={item.id}>
                <BellEntry
                  item={item}
                  title={bellTitle(item, follows.statusOf)}
                  by={bellBy(item)}
                  link={client.linkOf(item)}
                  onOpen={() => opened(item)}
                  onFollow={onClose}
                />
              </li>
            ))}
          </ul>
          {list.hasNextPage && (
            <div className="p-3">
              <Button
                variant="secondary"
                className="w-full"
                disabled={list.isFetchingNextPage}
                onClick={() => void list.fetchNextPage()}
              >
                {list.isFetchingNextPage ? m.loading_more() : m.load_more()}
              </Button>
            </div>
          )}
        </TabPanel>
        <TabPanel value="following" className="overflow-auto">
          <FollowingList client={follows} onOpen={onClose} />
        </TabPanel>
      </Tabs>
      <PushSection client={client.push} />
    </div>
  );
}

interface BellEntryProps {
  item: BellItem;
  /** What it says, in the app's words. */
  title: string;
  /** Who changed what it is about, when it says. */
  by: string | null;
  link: LinkOptions | null;
  onOpen: () => void;
  /** The item's page opens: the sheet closes. */
  onFollow: () => void;
}

/** An item: its page, if the app has one; otherwise a press only marks it as read. */
function BellEntry({ item, title, by, link, onOpen, onFollow }: BellEntryProps) {
  const unread = item.readAt === null;
  const className = "flex w-full items-start gap-2 px-4 py-3 text-left text-sm hover:bg-accent";
  const content = (
    <>
      <span aria-hidden className={cn("mt-1.5 size-1.5 shrink-0 rounded-full", unread && "bg-brand")} />
      <span className="min-w-0 flex-1">
        <span className={cn("block break-words", unread && "font-medium")}>
          {title}
          {unread && <span className="sr-only">, {m.bell_unread_item()}</span>}
        </span>
        <span className="block break-words text-xs text-muted-foreground">
          {by ? `${by} · ${formatAge(item.createdAt)}` : formatAge(item.createdAt)}
        </span>
      </span>
    </>
  );
  if (!link) {
    return (
      <button type="button" className={className} onClick={onOpen}>
        {content}
      </button>
    );
  }
  return (
    <Link
      {...link}
      className={className}
      onClick={() => {
        onOpen();
        onFollow();
      }}
    >
      {content}
    </Link>
  );
}
